/*
  Variations, wave 3 (screen 11):
  - VariationTrailFeed     — the approval trail of one VO (#6): Proposed → Sent to the consultant → Approved, each
                             with its date, who recorded it, the reference and the attachment; a "waiting > 14 d"
                             flag; "Record step" and "Remove step" for people who edit variations
  - RecordStepDialog       — date, reference, consultant, note and an attachment for one step
  - VariationInvoicingCell — the Invoicing column of the table (#7): received / submitted / not invoiced with the
                             invoiced share, a link into Invoicing scoped to the VO and "Raise"
  - SiteSourceNote         — "Raised on site · Villa 4 (F) · Somar" with the site photos (#8)
*/
import { useEffect, useMemo, useState } from 'react';
import {
  CircleCheck, CircleX, FileText, Hourglass, Loader2, MapPin, Paperclip, Plus, ReceiptText, RotateCcw, Send, Trash2,
} from 'lucide-react';
import api, { openAuthenticatedFile } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Segmented } from '@/components/ui/segmented';
import { StatusPill, ToneDot } from '@/components/ui/status-pill';
import { RowActions } from '@/components/ui/row-actions';
import { ProgressBar } from '@/components/ui/progress-bar';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { notify } from '@/components/ui/notify';
import { getErrorMessage } from '@/lib/errors';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { MONO } from './ProjectUi';
import {
  RECORDABLE_STEPS, TRAIL_STEPS, attachmentUrl, decisionStep, todayGst, variationFollowUp, variationTrail,
} from './variationTrailModel';

const STEP_ICONS = { proposed: FileText, sent: Send, revise: RotateCcw, approved: CircleCheck, rejected: CircleX };
const STEP_DOT = {
  proposed: 'border-line bg-surface text-ink-3',
  sent: 'border-line bg-surface text-ink-2',
  revise: 'border-transparent bg-status-amber-bg text-status-amber-ink',
  approved: 'border-transparent bg-brand text-brand-fg',
  rejected: 'border-transparent bg-status-red-bg text-status-red-ink',
};

const openFile = async (url) => {
  try {
    await openAuthenticatedFile(url);
  } catch (err) {
    notify.error('Could not open the file', { description: getErrorMessage(err) });
  }
};

export function FollowUpFlag({ followUp, consultant, className }) {
  if (!followUp) return null;
  const party = followUp.party || consultant || 'the consultant';
  return (
    <span
      className={cn('inline-flex items-center gap-1 rounded-md bg-status-amber-bg px-1.5 py-0.5 text-[11.5px] font-semibold text-status-amber-ink', className)}
      title={`Sent ${formatDate(followUp.since)} — no answer from ${party} yet`}
      data-testid="variation-follow-up"
    >
      <Hourglass className="h-3 w-3" aria-hidden="true" />
      {followUp.days} d with {party}
    </span>
  );
}

export function VariationTrailFeed({ variation, projectId, consultant, canEdit, onChanged }) {
  const confirm = useConfirm();
  const [dialog, setDialog] = useState(null);
  const trail = useMemo(() => variationTrail(variation), [variation]);
  const followUp = variationFollowUp(variation);
  const decided = decisionStep(variation);
  const status = variation.status || 'Proposed';
  const missingDecision = (status === 'Approved' || status === 'Rejected') && !decided;
  const lastStep = trail[trail.length - 1]?.step;
  const suggested = lastStep === 'sent' || lastStep === 'revise' ? 'approved' : 'sent';

  const remove = async (entry) => {
    const ok = await confirm({
      title: `Remove “${entry.label}” from ${variation.vo_number || 'this VO'}?`,
      description: `${formatDate(entry.date)}${entry.reference ? ` · ref ${entry.reference}` : ''}. The VO's status stays as it is.`,
      tone: 'danger',
      confirmLabel: 'Remove step',
    });
    if (!ok) return;
    try {
      const res = await api.delete(`/projects/${projectId}/variations/${variation.id}/trail/${entry.id}`);
      notify.success(`${entry.label} removed from ${variation.vo_number || 'the VO'}`);
      onChanged?.(res.data);
    } catch (err) {
      notify.error('Could not remove the step', { description: getErrorMessage(err) });
    }
  };

  return (
    <section aria-label={`Approval trail of ${variation.vo_number || 'variation'}`} data-testid={`variation-trail-${variation.id}`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Approval trail</p>
        <FollowUpFlag followUp={followUp} consultant={consultant} />
      </div>
      <ol className="relative space-y-3 before:absolute before:bottom-2 before:left-[11px] before:top-2 before:w-px before:bg-line">
        {trail.map((entry) => {
          const Icon = STEP_ICONS[entry.step] || FileText;
          const file = entry.attachment?.file;
          return (
            <li key={entry.id} className="group/step relative flex items-start gap-2.5 pl-0" data-testid="variation-trail-step">
              <span className={cn('relative z-[1] grid h-6 w-6 shrink-0 place-items-center rounded-full border', STEP_DOT[entry.step] || STEP_DOT.proposed)} aria-hidden="true">
                <Icon className="h-3.5 w-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold leading-tight text-ink">
                  {entry.step === 'sent' && (entry.party || consultant) ? `Sent to consultant ${entry.party || consultant}` : entry.label}
                </p>
                <p className="mt-0.5 text-[12px] leading-snug text-ink-3">
                  {formatDate(entry.date)}
                  {entry.by ? ` · ${entry.by}` : ''}
                  {entry.reference ? <> · ref <span className={cn(MONO, 'text-[11.5px] text-ink-2')}>{entry.reference}</span></> : null}
                </p>
                {entry.note ? <p className="mt-0.5 text-[12px] text-ink-2">{entry.note}</p> : null}
                {file ? (
                  <button
                    type="button"
                    onClick={() => openFile(entry.attachment.url || attachmentUrl(projectId, file))}
                    className="mt-1 inline-flex max-w-full items-center gap-1 text-[12px] font-semibold text-brand-ink hover:underline"
                  >
                    <Paperclip className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    <span className="truncate">{entry.attachment.name || 'Attachment'}</span>
                  </button>
                ) : null}
              </div>
              {canEdit && !entry.derived ? (
                <RowActions
                  size="sm"
                  label={`Actions for step ${entry.label}`}
                  className="opacity-60 group-hover/step:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
                  items={[{ label: 'Remove step…', icon: Trash2, danger: true, onSelect: () => remove(entry) }]}
                />
              ) : null}
            </li>
          );
        })}
        {missingDecision ? (
          <li className="relative flex items-start gap-2.5" data-testid="variation-trail-missing">
            <span className="relative z-[1] grid h-6 w-6 shrink-0 place-items-center rounded-full border border-dashed border-line bg-surface text-ink-4" aria-hidden="true">
              {status === 'Approved' ? <CircleCheck className="h-3.5 w-3.5" /> : <CircleX className="h-3.5 w-3.5" />}
            </span>
            <div className="min-w-0 flex-1 text-[12.5px] text-ink-3">
              <b className="font-semibold text-ink-2">{status}</b> — date and reference not recorded.
              {canEdit ? (
                <button type="button" className="ml-1 font-semibold text-brand-ink hover:underline" onClick={() => setDialog(status === 'Approved' ? 'approved' : 'rejected')}>
                  Record {status === 'Approved' ? 'approval' : 'rejection'}
                </button>
              ) : null}
            </div>
          </li>
        ) : null}
      </ol>
      {canEdit ? (
        <Button type="button" variant="ghost" size="sm" className="mt-2 -ml-2" onClick={() => setDialog(suggested)} data-testid={`variation-record-step-${variation.id}`}>
          <Plus aria-hidden="true" />
          Record step
        </Button>
      ) : null}
      <RecordStepDialog
        open={Boolean(dialog)}
        onOpenChange={(open) => { if (!open) setDialog(null); }}
        initialStep={dialog || suggested}
        variation={variation}
        projectId={projectId}
        consultant={consultant}
        onSaved={(updated) => { setDialog(null); onChanged?.(updated); }}
      />
    </section>
  );
}

const FIELD_LABEL = 'text-[12.5px] font-semibold text-ink-2';

export function RecordStepDialog({ open, onOpenChange, initialStep = 'sent', variation, projectId, consultant, onSaved }) {
  const [step, setStep] = useState(initialStep);
  const [day, setDay] = useState(todayGst());
  const [reference, setReference] = useState('');
  const [party, setParty] = useState(consultant || '');
  const [note, setNote] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setStep(initialStep);
    setDay(todayGst());
    setReference('');
    setParty(consultant || '');
    setNote('');
    setFile(null);
    setError('');
  }, [open, initialStep, consultant]);

  const submit = async (event) => {
    event.preventDefault();
    if (!day) {
      setError('Pick the date of this step');
      return;
    }
    const form = new FormData();
    form.append('step', step);
    form.append('date', day);
    form.append('reference', reference.trim());
    form.append('party', step === 'sent' ? party.trim() : '');
    form.append('note', note.trim());
    if (file) form.append('file', file);
    setSaving(true);
    setError('');
    try {
      const res = await api.post(`/projects/${projectId}/variations/${variation.id}/trail`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      notify.success(`${variation.vo_number || 'VO'}: ${TRAIL_STEPS[step].label} recorded`);
      onSaved?.(res.data);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not record the step'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="variation-step-dialog">
        <DialogHeader>
          <DialogTitle>Record a step · {variation.vo_number || 'VO'}</DialogTitle>
          <DialogDescription>
            {step === 'approved' || step === 'rejected'
              ? `Recording “${TRAIL_STEPS[step].label}” also sets the VO's status.`
              : 'Dates and references show in the approval trail; a VO with the consultant for more than 14 days is flagged.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1">
            <span className={cn(FIELD_LABEL, 'block')}>Step</span>
            <Segmented
              ariaLabel="Step"
              value={step}
              onChange={setStep}
              options={RECORDABLE_STEPS.map((key) => ({
                key,
                label: key === 'sent' ? 'Sent' : key === 'revise' ? 'Revise' : TRAIL_STEPS[key].label,
                dot: TRAIL_STEPS[key].tone,
              }))}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block space-y-1">
              <span className={FIELD_LABEL}>Date</span>
              <Input type="date" value={day} max={todayGst()} onChange={(event) => setDay(event.target.value)} aria-label="Step date" required />
            </label>
            {step === 'sent' ? (
              <label className="block space-y-1">
                <span className={FIELD_LABEL}>Sent to</span>
                <Input value={party} onChange={(event) => setParty(event.target.value)} placeholder="Consultant" aria-label="Sent to" maxLength={120} />
              </label>
            ) : (
              <label className="block space-y-1">
                <span className={FIELD_LABEL}>Reference</span>
                <Input value={reference} onChange={(event) => setReference(event.target.value)} placeholder="BH.NS/LIV/VO-001" aria-label="Reference" maxLength={120} className={MONO} />
              </label>
            )}
          </div>
          {step === 'sent' ? (
            <label className="block space-y-1">
              <span className={FIELD_LABEL}>Reference <span className="font-normal text-ink-3">(transmittal, email subject)</span></span>
              <Input value={reference} onChange={(event) => setReference(event.target.value)} aria-label="Reference" maxLength={120} className={MONO} />
            </label>
          ) : null}
          <label className="block space-y-1">
            <span className={FIELD_LABEL}>Note</span>
            <Textarea value={note} onChange={(event) => setNote(event.target.value)} aria-label="Note" maxLength={500} className="min-h-[56px]" />
          </label>
          <label className="block space-y-1">
            <span className={FIELD_LABEL}>Attachment <span className="font-normal text-ink-3">(signed VO, letter, PDF or photo)</span></span>
            <Input type="file" accept=".pdf,.doc,.docx,.msg,.eml,image/*" onChange={(event) => setFile(event.target.files?.[0] || null)} aria-label="Attachment" />
          </label>
          {error ? <p className="text-[12.5px] font-semibold text-status-red-ink" role="alert" data-testid="variation-step-error">{error}</p> : null}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="variation-step-save">
              {saving ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
              Record {TRAIL_STEPS[step].label.toLowerCase()}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const BAR_TONE = { green: 'green', amber: 'amber', gray: 'gray' };

/** The Invoicing column (#7). `state` = variationInvoicing(vo, invoices). */
export function VariationInvoicingCell({ variation, state, onOpen, onRaise }) {
  const dot = state.kind === 'received' ? 'green' : state.kind === 'submitted' ? 'amber' : state.kind === 'scheduled' ? 'gray' : 'amber';
  return (
    <div className="min-w-[150px]" data-testid={`variation-invoicing-${variation.id}`}>
      <div className="flex items-center justify-between gap-2">
        {onOpen ? (
          <button
            type="button"
            onClick={() => onOpen(variation)}
            className="inline-flex min-w-0 items-center gap-1.5 text-left text-[12.5px] font-semibold text-ink hover:underline"
            title={`Open ${variation.vo_number || 'this VO'} in Invoicing`}
          >
            <ToneDot tone={dot} />
            <span className="truncate">{state.label}</span>
          </button>
        ) : (
          <span className="inline-flex min-w-0 items-center gap-1.5 text-[12.5px] font-semibold text-ink">
            <ToneDot tone={dot} />
            <span className="truncate">{state.label}</span>
          </span>
        )}
        {onRaise && state.canRaise ? (
          <button
            type="button"
            onClick={() => onRaise(variation)}
            className="inline-flex shrink-0 items-center gap-1 text-[12.5px] font-semibold text-brand-ink hover:underline"
            data-testid={`variation-raise-${variation.id}`}
          >
            <ReceiptText className="h-3.5 w-3.5" aria-hidden="true" />
            Raise
          </button>
        ) : null}
      </div>
      <ProgressBar value={state.percent} tone={BAR_TONE[state.tone] || 'gray'} label={`${variation.vo_number || 'VO'} invoiced`} className="mt-1.5" />
      <p className="mt-1 text-[11.5px] tabular-nums text-ink-3">
        {state.invoiced ? `${state.percent}% of ${state.value.toLocaleString('en-US', { maximumFractionDigits: 2 })}` : `0 of ${state.value.toLocaleString('en-US', { maximumFractionDigits: 2 })} invoiced`}
      </p>
    </div>
  );
}

/** Where a site VO came from, with its photos (#8). */
export function SiteSourceNote({ variation, projectId, showFinance = true }) {
  const source = variation.source;
  if (!source || source.kind !== 'site') return null;
  const photos = (source.photos || []).filter((photo) => photo && photo.file);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-line bg-surface px-3 py-2 text-[12.5px]" data-testid={`variation-site-source-${variation.id}`}>
      <span className="inline-flex items-center gap-1.5 font-semibold text-ink">
        <MapPin className="h-3.5 w-3.5 text-ink-3" aria-hidden="true" />
        Raised on site{source.location ? ` · ${source.location}` : ''}
      </span>
      <span className="text-ink-3">{[source.by, formatDate(source.at)].filter(Boolean).join(' · ')}</span>
      {/* "Needs pricing" speaks of the VO value: only for roles that see it (Invoicing) */}
      {showFinance && !variation.finance_hidden && variation.needs_pricing && !(Number(variation.total_value) > 0) ? <StatusPill tone="amber" label="Needs pricing" size="sm" /> : null}
      {photos.map((photo, index) => (
        <button
          key={photo.file}
          type="button"
          onClick={() => openFile(photo.url || attachmentUrl(projectId, photo.file))}
          className="inline-flex items-center gap-1 font-semibold text-brand-ink hover:underline"
        >
          <Paperclip className="h-3.5 w-3.5" aria-hidden="true" />
          Photo {index + 1}
        </button>
      ))}
    </div>
  );
}
